import { Component, OnChanges, Input, Output, ViewChild, EventEmitter, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsMessage } from '../shared/generic-modal/generic-modal.service';
import { ConversionTable } from './models/conversion-table';
import { ConversionTableColumn } from './models/conversion-table-column';
import { ConversionTableService } from './services/conversion-table.service';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { DxoEditingModule, DxiColumnModule } from 'devextreme-angular/ui/nested';

@Component({
    templateUrl: './partner-conversion-table-structure.component.html',
    selector: 'ep-partner-conversion-table-structure',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, PermissionRequiredDirective, DxDataGridModule, DxoEditingModule, DxiColumnModule]
})

export class PartnerConversionTableStructureComponent implements OnChanges {

    constructor(private conversionTableService: ConversionTableService, private loadingPanelService: LoadingPanelService, private modalService: GenericModalService, private renderer: Renderer2) { }

    @Output() saved: EventEmitter<ConversionTable> = new EventEmitter<ConversionTable>();
    @Output() cancelled: EventEmitter<ConversionTable> = new EventEmitter<ConversionTable>();

    @Input() partnerId: number;
    @Input() conversionTable: ConversionTable;

    @ViewChild(DxDataGridComponent, { static: true }) grid: DxDataGridComponent;

    public clonedConversionTable: ConversionTable;
    public removedColumns: ConversionTableColumn[] = [];

    ngOnChanges(changes: any) {
        if (changes.conversionTable) {
            this.init();
        }
    }

    init() {
        this.removedColumns = [];
        this.clonedConversionTable = Object.assign({}, this.conversionTable);
    }

    save(conversionTableForm) {
        if (!this.conversionTable.Id) //new conversion table
        {
            this.checkConversionTableExist(conversionTableForm.value.name);
        }
        else { //update
            if (this.conversionTable.Name != conversionTableForm.value.name) {
                this.checkConversionTableExist(conversionTableForm.value.name);
            }
            else {
                this.saveConversionTable();
            }
        }
    }

    checkConversionTableExist(conversionTableName: string) {
        this.conversionTableService.isConversionTableNameAvailable(conversionTableName).then((isAvailable: boolean) => {
            if (isAvailable) {
                this.saveConversionTable();
            }
            else {
                let settings = new ModalSettingsMessage('Conversion table name in use', 'The chosen name is not available.');
                this.modalService.message(settings).then((e) => { }).catch((e) => { });
                this.renderer.selectRootElement("#inputNameNewConversionTable").focus();
            }
        });
    }

    async saveConversionTable() {
        
        if (!this.clonedConversionTable) {
            return; 
        }

        if (!this.conversionTable?.IsUniversal && this.clonedConversionTable.IsUniversal && this.clonedConversionTable.Rows.length > 0) {
            try {
                await this.modalService.confirmDirect('Existing rows', 'The conversion table contains existing rows. Making the table universal will also make existing rows universal. Continue?');
            }
            catch { return; }
        }

        this.loadingPanelService.showLoadingPanelDirect('Saving Conversion Table...');

        this.grid.instance.saveEditData();

        this.removedColumns.forEach(deletedColumn => {
            deletedColumn.MarkForDeletion = true; // Mark the column to be deleted
            this.clonedConversionTable.Columns.push(deletedColumn); // Add it back to the collection of columns, so it will be transmitted to the backend and deleted in the database.
        });

        this.clonedConversionTable.Rows = []; // Make sure there are no rows - so we won't have to send a lot of unnecessary data to the backend.

        // If it's not Universal set the partner-id which is the value in the database indicating whether or not it's universal
        if (!this.clonedConversionTable.IsUniversal) {
            this.clonedConversionTable.PartnerId = this.partnerId;
        }
        else {
            this.clonedConversionTable.PartnerId = null;
        }

        await this.conversionTableService.saveConversionTableStructure(this.clonedConversionTable).then(savedConversionTable => {
            Object.assign(this.conversionTable, savedConversionTable); // Merge the changes into the bound conversion-table
            this.saved.emit(this.conversionTable); // Notify that conversion table has been successfully saved
            this.clonedConversionTable = null;
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    cancel() {
        this.clonedConversionTable = null;
        this.cancelled.emit();
    }

    onRowRemoved(e: any) {
        if (e.data.ColumnId) {
            this.removedColumns.push(e.data);
        }
    }

    onInitNewRow(e: any) {
        e.data.SortOrder = this.clonedConversionTable.Columns.length > 0 ? Math.max.apply(null, this.clonedConversionTable.Columns.map(c => c.SortOrder)) + 1 : 1; // Get the highest sort-order-value and add +1
        e.data.ConversionTableId = this.clonedConversionTable.Id;
    }
}
